import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { meetingsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Mail, CheckCircle2, AlertCircle } from 'lucide-react';

export function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If not authenticated, redirect to login with return URL
    if (!isAuthenticated) {
      const returnUrl = `/reunioes/invite/${token}`;
      toast.info('Faça login para aceitar o convite');
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // Auto-accept invite if authenticated
    acceptInvite();
  }, [isAuthenticated, token]);

  const acceptInvite = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await meetingsAPI.acceptInvite(token);
      setMeeting(response.data);
      toast.success('Convite aceito com sucesso!');
      
      // Auto-redirect after 2 seconds
      setTimeout(() => {
        navigate(`/reunioes/${response.data.slug}`);
      }, 2000);
    } catch (error) {
      console.error('Failed to accept invite:', error);
      
      if (error.response?.status === 404) {
        setError('Convite não encontrado');
      } else if (error.response?.status === 400) {
        const detail = error.response?.data?.detail;
        if (detail?.includes('expirado')) {
          setError('Este convite expirou');
        } else if (detail?.includes('utilizado')) {
          setError('Este convite já foi utilizado');
        } else {
          setError(detail || 'Convite inválido');
        }
      } else {
        setError('Erro ao aceitar convite');
      }
      
      toast.error(error.response?.data?.detail || 'Erro ao aceitar convite');
    } finally {
      setLoading(false);
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center py-6">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Processando convite...</h3>
              <p className="text-sm text-muted-foreground">
                Aguarde enquanto verificamos seu convite
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-center">Convite Inválido</CardTitle>
            <CardDescription className="text-center">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => navigate('/reunioes')}
                data-testid="go-to-meetings-button"
              >
                Ver Salas Disponíveis
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/feed')}
              >
                Voltar ao Feed
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-center">Convite Aceito!</CardTitle>
            <CardDescription className="text-center">
              Você agora tem acesso à sala <strong>{meeting.title}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-center text-muted-foreground">
                Redirecionando para a sala em instantes...
              </p>
              <Button
                className="w-full"
                onClick={() => navigate(`/reunioes/${meeting.slug}`)}
                data-testid="enter-meeting-button"
              >
                Entrar na Sala Agora
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
